import { Object3D, Quaternion, Vector3 } from 'three/webgpu'
import { easing } from 'maath'
import ClawRod from './ClawRod'

export default class ClawRig {
	static defaults = {
		body: 'body002',
		plunger: 'plunger001',
		fingers: ['Cube103', 'Cube107', 'Cube111'],
		grip: 'Empty011',
		hook: 'Empty014',
		rod: 'body003',
		cableTip: 'Bone010',
		lift: 6,
		gravity: 30,
		damping: 0.925,
		iterations: 20,
		settle: 240,
		step: 1 / 30,
		trail: 1,
		slack: 1,
	}

	constructor(model) {
		this.options = ClawRig.defaults
		this.model = model
		this.body = model.getObjectByName(this.options.body)
		this.plunger = model.getObjectByName(this.options.plunger)
		this.fingers = this.options.fingers.map(name => model.getObjectByName(name))
		this.grip = model.getObjectByName(this.options.grip)
		this.tilt = new Quaternion()
		this.aim = new Vector3()
		this.#cable()
		this.rod = new ClawRod(model.getObjectByName(this.options.rod), model)
		this.pivot = this.rod.mesh.position.y + this.rod.bottom
	}

	#cable() {
		const { bones } = this.model.getObjectByProperty('isSkinnedMesh', true).skeleton
		const tip = bones.find(bone => bone.name === this.options.cableTip)

		this.hook = this.model.getObjectByName(this.options.hook)
		this.chain = []
		for (let bone = tip; bones.includes(bone); bone = bone.parent) this.chain.unshift(bone)
		this.anchor = this.chain[0].parent
		this.anchor.position.y += this.options.lift
		this.home = this.anchor.position.clone()

		this.tail = new Object3D()
		this.tail.position.copy(tip.position)
		tip.add(this.tail)

		const nodes = [...this.chain, this.tail]

		this.model.updateMatrixWorld(true)
		this.points = nodes.map(node => this.#local(node, new Vector3()))
		this.previous = this.points.map(point => point.clone())
		this.lengths = this.points.slice(1).map((point, i) => point.distanceTo(this.points[i]))
		this.axes = nodes.slice(1).map(child => child.position.clone().normalize())
		this.velocity = new Vector3()
		this.segment = new Vector3()
		this.direction = new Vector3()
		this.modelQuaternion = new Quaternion()
		this.hookPoint = this.#local(this.hook, new Vector3())
		this.offset = this.anchor.position.x - this.hookPoint.x
		this.reach = this.lengths.reduce((sum, length) => sum + length, 0) * this.options.slack
		this.parentQuaternion = new Quaternion()

		for (let i = 0; i < this.options.settle; i++) this.#simulate(1 / 60)
		this.#pose()
	}

	#follow(delta) {
		const { position } = this.anchor

		this.#local(this.hook, this.hookPoint)
		this.segment.set(this.hookPoint.x + this.offset, this.home.y, this.home.z)
		easing.damp3(position, this.segment, this.options.trail, delta)

		this.segment.subVectors(position, this.hookPoint)
		if (this.segment.length() > this.reach) position.copy(this.hookPoint).add(this.segment.setLength(this.reach))
	}

	#local(node, target) {
		return this.model.worldToLocal(node.getWorldPosition(target))
	}

	#simulate(dt) {
		const { points, previous, lengths } = this
		const { gravity, damping, iterations } = this.options
		const last = points.length - 1

		this.#local(this.chain[0], points[0])
		this.#local(this.hook, points[last])

		for (let i = 1; i < last; i++) {
			this.velocity.subVectors(points[i], previous[i]).multiplyScalar(damping)
			previous[i].copy(points[i])
			points[i].add(this.velocity)
			points[i].y -= gravity * dt * dt
		}

		for (let k = 0; k < iterations; k++) {
			for (let i = 0; i < last; i++) {
				const start = i === 0 ? 0 : 1
				const end = i + 1 === last ? 0 : 1

				if (start + end === 0) continue

				this.segment.subVectors(points[i + 1], points[i])
				const distance = this.segment.length() || 1e-6
				const error = (distance - lengths[i]) / distance / (start + end)

				points[i].addScaledVector(this.segment, error * start)
				points[i + 1].addScaledVector(this.segment, -error * end)
			}
		}
	}

	#pose() {
		this.model.getWorldQuaternion(this.modelQuaternion)

		for (let i = 0; i < this.chain.length; i++) {
			const bone = this.chain[i]

			bone.parent.getWorldQuaternion(this.parentQuaternion).invert()
			this.direction
				.subVectors(this.points[i + 1], this.points[i])
				.normalize()
				.applyQuaternion(this.modelQuaternion)
				.applyQuaternion(this.parentQuaternion)
			bone.quaternion.setFromUnitVectors(this.axes[i], this.direction)
		}
	}

	update(delta) {
		this.model.updateMatrixWorld(true)
		this.grip.getWorldPosition(this.aim)

		for (const finger of this.fingers) {
			finger.up.set(0, 1, 0).applyQuaternion(finger.parent.getWorldQuaternion(this.tilt))
			finger.lookAt(this.aim)
			finger.rotateY(Math.PI)
			finger.updateMatrixWorld(true)
		}

		this.rod.update(delta)
		this.#follow(delta)
		this.#simulate(Math.min(delta, this.options.step))
		this.#pose()
	}
}
